import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Code Assurance screen (task 7.4; design §10, requirement 13.4). Three operator-API
 * operations back the screen, all role `viewer`, taken straight from the generated
 * `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/assurance/summary?from=…&to=…` → {@link AssuranceSummary}: scans by verdict and by
 *   surface, open findings by severity and by engine (deduped on `fingerprint`, latest status wins),
 *   the block rate and the mean time to fix. Every breakdown is a `Quantity` and zero-filled over its
 *   catalog, so the screen's tiles always read against a complete set of catalog members. This is the
 *   source for the headline tiles.
 * - `GET /api/v1/assurance/findings?from=…&to=…&severity=&engine=&status=&agent=` →
 *   {@link AssuranceFindingPage}: one page of findings (deduped on fingerprint, latest status wins)
 *   with `by_severity`/`by_engine` counts, filterable by severity, engine, status and agent. This is
 *   the source for the filterable findings table AND the agents-ranked-by-findings breakdown (which
 *   is derived from the page's `items`, grouped by `agent_sub`).
 * - `GET /api/v1/assurance/findings/{fingerprint}` → {@link AssuranceFindingDetail}: one finding with
 *   its decision history (`decisions`, newest first). This is lazily fetched only when a finding is
 *   selected (its fingerprint is non-null), so opening the detail drawer triggers the read; it is the
 *   source for the per-finding decision history.
 *
 * The summary and findings reads are WINDOWED (they take `from`/`to`), so — exactly like the
 * Overview/Decisions/Identity/Enforcement/Posture screens — the window is resolved from the shared
 * time-range and pausing freezes the queries: in live mode "now" is bucketed to a 10s grid to keep
 * the query key stable between ticks; when frozen (paused or custom) `resolveWindow` returns the exact
 * frozen window and the key stops advancing (design 12.2, FR-13 AC2). The finding-detail read is NOT
 * windowed by contract (it is keyed by fingerprint), so its decision history is independent of the
 * current window — a decision made before the window still appears, which is the honest behaviour for
 * a finding's full history.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and the
 * shell shows "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The contract `AssuranceSummary` response body (tiles source). */
export type AssuranceSummary = components["schemas"]["AssuranceSummary"];
/** A single scans-by-verdict row (verdict + count). */
export type ScansByVerdictRow = AssuranceSummary["scans_by_verdict"][number];
/** A single scans-by-surface row (surface + count). */
export type ScansBySurfaceRow = AssuranceSummary["scans_by_surface"][number];
/** A single open-findings-by-severity row (severity + count). */
export type OpenFindingsBySeverityRow = AssuranceSummary["open_findings_by_severity"][number];
/** A single open-findings-by-engine row (engine + count). */
export type OpenFindingsByEngineRow = AssuranceSummary["open_findings_by_engine"][number];

/** The contract `AssuranceFindingPage` response body (findings table + counts source). */
export type AssuranceFindingPage = components["schemas"]["AssuranceFindingPage"];
/** A single assurance finding row (deduped on fingerprint, latest status wins). */
export type AssuranceFinding = components["schemas"]["AssuranceFinding"];

/** The contract `AssuranceFindingDetail` response body (finding + decision history). */
export type AssuranceFindingDetail = components["schemas"]["AssuranceFindingDetail"];
/** A single decision taken on a finding. */
export type AssuranceDecision = components["schemas"]["AssuranceDecision"];

/** The server-side filters the findings list accepts (all optional; omit = no filter). */
export interface AssuranceFindingFilters {
  readonly severity?: components["schemas"]["enum_finding_severity"];
  readonly engine?: components["schemas"]["enum_assurance_engine"];
  readonly status?: components["schemas"]["enum_finding_status"];
  /** Pseudonymous agent reference. */
  readonly agent?: string;
}

/**
 * Return a copy of `filters` with `key` set to `value`, or with the key REMOVED when `value` is
 * undefined. This keeps the filters object compatible with `exactOptionalPropertyTypes` — an absent
 * filter is an absent key, never a key whose value is explicitly `undefined` — so clearing a filter
 * does not leave a `{ severity: undefined }` the compiler rejects.
 */
export function setFilter<K extends keyof AssuranceFindingFilters>(
  filters: AssuranceFindingFilters,
  key: K,
  value: AssuranceFindingFilters[K] | undefined,
): AssuranceFindingFilters {
  const next: AssuranceFindingFilters = { ...filters };
  if (value === undefined) {
    delete (next as Record<string, unknown>)[key as string];
  } else {
    (next as Record<string, unknown>)[key as string] = value;
  }
  return next;
}

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface AssuranceWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseAssuranceSummaryResult {
  readonly query: UseQueryResult<AssuranceSummary, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: AssuranceWindow;
}

export interface UseAssuranceFindingsResult {
  readonly query: UseQueryResult<AssuranceFindingPage, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: AssuranceWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): AssuranceWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the assurance summary for the current window. The query key carries the window so a frozen
 * window never refetches and a live window advances on the 10s grid; `staleTime` / `refetchInterval`
 * match the other screens (live tolerates a little staleness, frozen never goes stale).
 */
export function useAssuranceSummary(): UseAssuranceSummaryResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<AssuranceSummary, ApiError>({
    queryKey: ["assurance-summary", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<AssuranceSummary>("/assurance/summary", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does not
    // blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the findings page for the current window and the given filters. The query key carries both
 * the window AND every filter, so changing a filter is a distinct cache entry (and never shows a
 * stale, differently-filtered page). The first page only — `next_cursor` is left for a later
 * pagination affordance, matching the posture/identity screens.
 */
export function useAssuranceFindings(
  filters: AssuranceFindingFilters,
): UseAssuranceFindingsResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<AssuranceFindingPage, ApiError>({
    queryKey: [
      "assurance-findings",
      window.fromMs,
      window.toMs,
      filters.severity ?? null,
      filters.engine ?? null,
      filters.status ?? null,
      filters.agent ?? null,
    ],
    queryFn: ({ signal }) =>
      apiFetch<AssuranceFindingPage>("/assurance/findings", {
        query: {
          from: window.fromMs,
          to: window.toMs,
          severity: filters.severity,
          engine: filters.engine,
          status: filters.status,
          agent: filters.agent,
        },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch one finding's full detail (the finding plus its decision history, newest first) by
 * fingerprint. It is `enabled` only when a fingerprint is given, so the read fires lazily when a
 * finding is selected (the detail drawer opens) and not before. The detail is keyed solely by
 * fingerprint — it is NOT windowed — so a finding's complete decision history is shown regardless of
 * the current time range, which is the honest behaviour for a finding's lifecycle. The first page of
 * decisions only; `next_cursor` is left for a later affordance, matching the findings list.
 */
export function useAssuranceFinding(
  fingerprint: string | null,
): UseQueryResult<AssuranceFindingDetail, ApiError> {
  return useQuery<AssuranceFindingDetail, ApiError>({
    queryKey: ["assurance-finding", fingerprint],
    enabled: fingerprint !== null,
    queryFn: ({ signal }) =>
      apiFetch<AssuranceFindingDetail>(
        `/assurance/findings/${encodeURIComponent(fingerprint as string)}`,
        { signal },
      ),
    // A finding's history does not change on the live tick; keep it stable while the drawer is open.
    staleTime: Infinity,
  });
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
